import type { z } from 'zod'

import type { ApiError, GenericApiError, SerializedError } from './errors'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** One route's auth and schemas; its path and method are its keys in a `RouteRegistry`. */
export interface RouteDefinition {
  auth:
    | 'staff'
    | 'superuser'
    | 'member'
    | 'impersonatedMember'
    | 'bookingToken'
    | 'apiKey'
    | 'public'
  params?: z.ZodObject
  /** One object, or a union of objects when the query has distinct shapes. */
  query?: z.ZodObject | z.ZodUnion<readonly z.ZodObject[]>
  body?: z.ZodType
  /** `null` answers 204 with no body. */
  response: z.ZodType | FileResponseDefinition | null
  status?: 200 | 201
  /** Domain errors the route can answer with. Generic errors (`GENERIC_API_ERRORS`) are implied. */
  errors?: readonly ApiErrorClass[]
}

/** A response that is a file rather than JSON; its handler returns a `FileBody`. */
export type FileResponseDefinition = { contentType: string }

/** Declares a route that answers with a file of this content type. */
export function file(contentType: string): FileResponseDefinition {
  return { contentType }
}

/** What a file route's handler returns. */
export type FileBody = { body: ArrayBuffer | ReadableStream; fileName: string }

export type ApiErrorClass = abstract new (...args: never[]) => ApiError

/** An audience's routes keyed by full path, then method. Declare with `as const satisfies RouteRegistry`. */
export type RouteRegistry = Record<string, Partial<Record<HttpMethod, RouteDefinition>>>

/** Paths in `R` that define method `M`. */
export type PathsFor<R extends RouteRegistry, M extends HttpMethod> = {
  [P in keyof R]: R[P] extends Record<M, RouteDefinition> ? P : never
}[keyof R] &
  string

export type DefinitionAt<R extends RouteRegistry, P extends keyof R, M extends HttpMethod> =
  R[P] extends Record<M, infer D extends RouteDefinition> ? D : never

type InputPart<D, K extends 'params' | 'query' | 'body'> =
  D extends Record<K, infer S extends z.ZodType>
    ? Record<never, never> extends z.input<S>
      ? { [P in K]?: z.input<S> }
      : { [P in K]: z.input<S> }
    : unknown

type InputParts<D> = InputPart<D, 'params'> & InputPart<D, 'query'> & InputPart<D, 'body'>

/** What a caller passes: only the keys the definition declares; a key is optional when its schema accepts `{}`. */
export type RouteInput<D extends RouteDefinition> = keyof InputParts<D> extends never
  ? { params?: never; query?: never; body?: never }
  : InputParts<D>

/** The input argument of a call: optional when `RouteInput<D>` accepts an empty object. */
export type RouteInputArgs<D extends RouteDefinition> =
  Record<never, never> extends RouteInput<D> ? [input?: RouteInput<D>] : [input: RouteInput<D>]

/** What a caller receives; a file route gives a `File` named by the server. */
export type RouteResponse<D extends RouteDefinition> = D extends {
  response: infer S extends z.ZodType
}
  ? z.output<S>
  : D extends { response: FileResponseDefinition }
    ? File
    : undefined

/** Fills `:name` segments with URI-encoded params. */
export function interpolatePath(path: string, params?: Record<string, unknown>) {
  return path.replace(/:(\w+)/g, (_, key: string) => {
    const value = params?.[key]
    if (value == null) throw new Error(`Missing path param "${key}" for ${path}`)
    return encodeURIComponent(String(value))
  })
}

/** The error bodies a route can answer with: generic ones, plus the domain errors it declares. */
export type RouteError<D extends RouteDefinition> =
  | GenericApiError
  | (D extends { errors: readonly (infer C)[] }
      ? C extends (abstract new (
          ...args: never[]
        ) => infer E extends ApiError)
        ? SerializedError<E>
        : never
      : never)
